import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Bookmark, Heart, FileText } from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { getAllLocalSaves, toggleLocalSave, hasLocalLike } from '../lib/localUserState';
import { getCurriculum } from '../constants/curricula';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Saved = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState([]);

  useEffect(() => {
    loadSaved();
  }, []);

  const loadSaved = async () => {
    setLoading(true);
    const ids = getAllLocalSaves();
    setSavedIds(ids);
    if (ids.length === 0) {
      setResources([]);
      setLoading(false);
      return;
    }
    try {
      const fetched = await Promise.all(
        ids.map((rid) => axios.get(`${API}/resources/${rid}`).then((r) => r.data).catch(() => null))
      );
      setResources(fetched.filter(Boolean));
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const removeSave = (id) => {
    toggleLocalSave(id);
    setResources((rs) => rs.filter((r) => r.id !== id));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Bookmark className="h-7 w-7 text-primary" strokeWidth={1.5} />
            <h1 className="text-4xl font-semibold tracking-tight">Saved on this device</h1>
          </div>
          <p className="text-foreground/70 leading-relaxed">
            Resources you saved are stored in this browser. Clearing browser data or switching device will remove them.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-20" data-testid="saved-empty">
            <div className="w-20 h-20 mx-auto rounded-full bg-accent flex items-center justify-center mb-6">
              <Bookmark className="h-10 w-10 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold mb-2">No saved resources yet</h3>
            <p className="text-foreground/70 mb-6">Click the bookmark icon on any resource to save it here.</p>
            <Link to="/resources">
              <Button>Browse Resources</Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="saved-grid">
            {resources.map((resource) => {
              const rCurr = getCurriculum(resource.curriculum);
              return (
                <div key={resource.id} className="border rounded-lg p-6 bg-surface hover:-translate-y-0.5 transition-all duration-200 hover:shadow-sm">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    {rCurr && (
                      <span className={`text-xs px-2 py-0.5 rounded font-mono ${rCurr.badge}`}>
                        {resource.curriculum}{resource.level ? ` · ${resource.level}` : ''}
                      </span>
                    )}
                    <span className="text-xs uppercase tracking-wider font-mono text-foreground/70">{resource.subject}</span>
                  </div>
                  <Link to={`/resources/${resource.id}`}>
                    <h3 className="text-lg font-semibold mb-2 hover:text-primary transition-colors">{resource.title}</h3>
                  </Link>
                  {resource.description && (
                    <p className="text-sm text-foreground/75 mb-4 line-clamp-2 leading-relaxed">{resource.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                        {(resource.uploader_name || 'A')[0].toUpperCase()}
                      </div>
                      <span className="text-sm text-foreground/85">{resource.uploader_name || 'Anonymous'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center text-sm text-foreground/70">
                        <Heart className="h-4 w-4 mr-1" strokeWidth={1.5} fill={hasLocalLike(resource.id) ? 'currentColor' : 'none'} />
                        {resource.likes_count}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSave(resource.id)}
                        data-testid={`saved-remove-${resource.id}`}
                      >
                        <Bookmark className="h-4 w-4 text-primary" strokeWidth={1.5} fill="currentColor" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
